import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, MessageSquare, Phone, X } from "lucide-react";
import { PHONE, TEL } from "./data";
import { DISCLAIMER, EXTRA_PRICES, MAIN_PRICES } from "./pricingData";

const SMS = "sms:+18645181871";
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "");

const PriceCard = ({ p, i }) => (
  <motion.div
    data-testid={`pricing-card-${slug(p.name)}`}
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: 0.05 + i * 0.03, ease: [0.16, 1, 0.3, 1] }}
    className="group flex flex-col justify-between gap-4 rounded-2xl border border-white/10 bg-[#0D0D0B] p-5 transition-colors duration-300 hover:border-[#FFB000]"
  >
    <div>
      <h4 className="text-lg font-extrabold uppercase leading-tight tracking-tight text-white">{p.name}</h4>
      <p className="mt-2 text-sm text-[#d6d2c8]">{p.desc}</p>
    </div>
    <div className="flex items-end justify-between border-t border-white/10 pt-3">
      <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#bdb9af]">Starting at</span>
      <span data-testid={`pricing-price-${slug(p.name)}`} className="font-display text-5xl leading-none text-[#FFB000]">${p.price}</span>
    </div>
  </motion.div>
);

const Group = ({ title, items, offset }) => (
  <div>
    <div className="mb-4 flex items-center gap-3">
      <span className="h-6 w-1.5 bg-[#FFB000]" />
      <h3 className="font-display text-3xl uppercase tracking-wide text-white sm:text-4xl">{title}</h3>
    </div>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((p, i) => <PriceCard key={p.name} p={p} i={i + offset} />)}
    </div>
  </div>
);

const EstimateCta = () => {
  const [choose, setChoose] = useState(false);
  return (
    <div className="rounded-2xl bg-[#FFB000] p-5 text-[#0D0D0B] sm:p-7">
      <button
        data-testid="pricing-free-estimate-button"
        onClick={() => setChoose((v) => !v)}
        className="font-display w-full rounded-xl bg-[#0D0D0B] px-4 py-5 text-3xl uppercase leading-none tracking-wide text-[#FFB000] transition-transform active:scale-[0.98] sm:text-5xl"
      >
        Get Your Free Estimate Today!
      </button>
      <AnimatePresence initial={false}>
        {choose && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="grid gap-3 pt-3 sm:grid-cols-2">
              <a data-testid="pricing-estimate-call" href={TEL} className="flex items-center justify-center gap-2 rounded-xl border-2 border-[#0D0D0B] py-4 text-lg font-black uppercase"><Phone className="h-5 w-5" strokeWidth={2.75} />Call {PHONE}</a>
              <a data-testid="pricing-estimate-text" href={SMS} className="flex items-center justify-center gap-2 rounded-xl border-2 border-[#0D0D0B] py-4 text-lg font-black uppercase"><MessageSquare className="h-5 w-5" strokeWidth={2.75} />Text {PHONE}</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
        <p className="text-center text-base font-extrabold sm:text-left">Call or text <span className="tabular-nums">{PHONE}</span></p>
        <a data-testid="pricing-call-now-button" href={TEL} className="flex items-center gap-2 rounded-full bg-[#0D0D0B] px-6 py-3 font-black uppercase text-white transition-transform active:scale-95">
          <Phone className="h-4 w-4" strokeWidth={2.75} />Call Now
        </a>
      </div>
    </div>
  );
};

export const PricingPanel = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    window.__lenis?.stop();
    return () => { window.removeEventListener("keydown", onKey); window.__lenis?.start(); };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="pricing-backdrop"
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            key="pricing-panel"
            id="pricing-panel"
            role="dialog"
            aria-label="Pricing"
            data-testid="pricing-panel"
            data-lenis-prevent
            initial={{ opacity: 0, y: -16, clipPath: "inset(0 0 100% 0)" }}
            animate={{ opacity: 1, y: 0, clipPath: "inset(0 0 0% 0)" }}
            exit={{ opacity: 0, y: -12, clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-x-0 top-[104px] z-40 max-h-[calc(100dvh-104px)] overflow-y-auto overscroll-contain border-b-4 border-[#FFB000] bg-[#161612] lg:top-16 lg:max-h-[calc(100dvh-64px)]"
          >
            <div className="mx-auto max-w-7xl space-y-10 px-4 pb-28 pt-8 sm:px-6 sm:pt-10 md:pb-10">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#FFB000]">Cazares Junk Removal</p>
                  <h2 className="font-display mt-2 text-5xl uppercase leading-none text-white sm:text-7xl">Pricing</h2>
                  <p className="mt-2 text-base text-[#d6d2c8]">Starting prices for every service. Free onsite estimates.</p>
                </div>
                <button data-testid="pricing-close-button" onClick={onClose} aria-label="Close pricing" className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#FFB000] text-[#0D0D0B] transition-transform hover:rotate-90">
                  <X className="h-6 w-6" strokeWidth={2.75} />
                </button>
              </div>
              <Group title="Main Junk Removal Services" items={MAIN_PRICES} offset={0} />
              <Group title="Additional Services" items={EXTRA_PRICES} offset={MAIN_PRICES.length} />
              <EstimateCta />
              <p data-testid="pricing-disclaimer" className="flex gap-3 rounded-2xl border border-white/15 p-5 text-sm font-semibold text-[#F2EEE6] sm:text-base">
                <AlertCircle className="h-5 w-5 shrink-0 text-[#FFB000]" />{DISCLAIMER}
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
